import Dexie, { type Table } from 'dexie';
import type { StorageBackend } from '@examflow/core';

interface BankFile {
  path: string;
  content: string;
  updatedAt: number;
}

class BankStorageDatabase extends Dexie {
  files!: Table<BankFile>;

  constructor() {
    super('ExamFlowBankStorageDB');
    this.version(1).stores({
      files: 'path, updatedAt',
    });
  }
}

const db = new BankStorageDatabase();

async function migrateFromLocalStorage(): Promise<void> {
  const prefix = 'examflow_banks:';
  const migrationKey = 'examflow_banks_migrated_to_dexie';

  if (localStorage.getItem(migrationKey) === 'true') {
    return;
  }

  try {
    const keysToMigrate: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToMigrate.push(key);
      }
    }

    if (keysToMigrate.length === 0) {
      localStorage.setItem(migrationKey, 'true');
      return;
    }

    const files: BankFile[] = [];
    for (const key of keysToMigrate) {
      const content = localStorage.getItem(key);
      if (content) {
        files.push({
          path: key.substring(prefix.length),
          content,
          updatedAt: Date.now(),
        });
      }
    }

    if (files.length > 0) {
      await db.files.bulkPut(files);
    }

    localStorage.setItem(migrationKey, 'true');
  } catch {
    // Migration failures are retried on next startup.
  }
}

let migrationPromise: Promise<void> | null = null;
function ensureMigration(): Promise<void> {
  if (!migrationPromise) {
    migrationPromise = migrateFromLocalStorage();
  }
  return migrationPromise;
}

export class DexieBackend implements StorageBackend {
  private migrationDone = false;

  private async ensureMigration(): Promise<void> {
    if (!this.migrationDone) {
      await ensureMigration();
      this.migrationDone = true;
    }
  }

  async readFile(path: string): Promise<string | null> {
    await this.ensureMigration();
    try {
      const file = await db.files.get(path);
      return file?.content || null;
    } catch (error) {
      console.error('[DexieBackend] Failed to read file:', path, error);
      return null;
    }
  }

  async writeFile(path: string, content: string): Promise<void> {
    await this.ensureMigration();
    try {
      await db.files.put({
        path,
        content,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error('[DexieBackend] Failed to write file:', path, error);
      throw error;
    }
  }

  async deleteFile(path: string): Promise<void> {
    await this.ensureMigration();
    try {
      await db.files.delete(path);
    } catch (error) {
      console.error('[DexieBackend] Failed to delete file:', path, error);
      throw error;
    }
  }

  async exists(path: string): Promise<boolean> {
    await this.ensureMigration();
    try {
      const file = await db.files.get(path);
      return file !== undefined;
    } catch (error) {
      console.error('[DexieBackend] Failed to check file existence:', path, error);
      return false;
    }
  }

  async mkdir(_path: string): Promise<void> {
    await this.ensureMigration();
  }

  async rmdir(path: string): Promise<void> {
    await this.ensureMigration();
    try {
      const files = await db.files.where('path').startsWith(path).toArray();
      const keys = files.map((file) => file.path);
      await db.files.bulkDelete(keys);
    } catch (error) {
      console.error('[DexieBackend] Failed to delete directory:', path, error);
      throw error;
    }
  }

  async readdir(path: string): Promise<string[]> {
    await this.ensureMigration();
    try {
      const files = await db.files.where('path').startsWith(path).toArray();
      return files.map((file) => file.path);
    } catch (error) {
      console.error('[DexieBackend] Failed to list directory:', path, error);
      return [];
    }
  }

  async clearAll(): Promise<void> {
    try {
      await db.files.clear();
    } catch (error) {
      console.error('[DexieBackend] Failed to clear all:', error);
      throw error;
    }
  }

  async getStorageUsage(): Promise<{ totalSize: number; fileCount: number }> {
    try {
      const files = await db.files.toArray();
      return {
        totalSize: files.reduce((sum, file) => sum + file.content.length, 0),
        fileCount: files.length,
      };
    } catch (error) {
      console.error('[DexieBackend] Failed to get storage usage:', error);
      return { totalSize: 0, fileCount: 0 };
    }
  }
}
